import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Check, MessageCircle, Pencil, Search, Send } from 'lucide-react';

function initials(name = '') {
  return name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase() || '·';
}

function shortTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
}

export default function MessengerPanel({
  threads = [],
  activeKey = '',
  onSelect,
  messages = [],
  myRole,
  labels = {},
  onSend,
  body,
  setBody,
  contacts = [],
  contactKey = '',
  onPickContact,
  contactsTitle = '',
  requireContact = false,
  busy = false,
}) {
  const bubblesRef = useRef(null);
  const nearBottomRef = useRef(true);
  const lastKeyRef = useRef(activeKey);
  const [contactQuery, setContactQuery] = useState('');
  const [threadQuery, setThreadQuery] = useState('');
  const [contactsOpen, setContactsOpen] = useState(true);
  const active = threads.find((t) => t.key === activeKey);
  const pickedContact = contacts.find((c) => c.key === contactKey);
  const title = active?.name || labels.newMessage || 'Nouveau message';

  const q = contactQuery.trim().toLowerCase();
  const filteredContacts = q
    ? contacts.filter((c) => `${c.name} ${c.sub || ''}`.toLowerCase().includes(q))
    : contacts;

  const tq = threadQuery.trim().toLowerCase();
  const filteredThreads = tq
    ? threads.filter((t) => t.name.toLowerCase().includes(tq))
    : threads;

  useEffect(() => {
    setContactQuery('');
    setContactsOpen(true);
  }, [activeKey]);

  const handleScroll = () => {
    const el = bubblesRef.current;
    if (el) nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useEffect(() => {
    const el = bubblesRef.current;
    if (!el) return;
    if (activeKey !== lastKeyRef.current) {
      lastKeyRef.current = activeKey;
      nearBottomRef.current = true;
    }
    if (nearBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, activeKey]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <div className={`msg-app${activeKey ? ' has-thread' : ''}`}>
      <aside className="msg-side">
        <div className="msg-side-head">
          <span>{labels.conversations || 'Conversations'}</span>
          <button
            type="button"
            className="msg-new"
            title={labels.newMessage || 'Nouveau message'}
            onClick={() => onSelect('new')}
          >
            <Pencil size={16} />
          </button>
        </div>
        {threads.length > 0 && (
          <div className="msg-side-search">
            <Search size={15} />
            <input
              type="text"
              value={threadQuery}
              onChange={(e) => setThreadQuery(e.target.value)}
              placeholder={labels.searchContact || 'Rechercher…'}
            />
          </div>
        )}
        <div className="msg-list">
          {threads.length === 0 && <p className="msg-empty-list">{labels.empty || 'Aucune conversation.'}</p>}
          {threads.length > 0 && filteredThreads.length === 0 && (
            <p className="msg-empty-list">{labels.empty || 'Aucun résultat.'}</p>
          )}
          {filteredThreads.map((thread) => (
            <button
              key={thread.key}
              type="button"
              className={`msg-item${activeKey === thread.key ? ' is-active' : ''}`}
              onClick={() => onSelect(thread.key)}
            >
              <span className="msg-avatar">{initials(thread.name)}</span>
              <span className="msg-item-meta">
                <span className="msg-item-name">{thread.name}</span>
                <span className="msg-item-preview">{thread.preview}</span>
              </span>
              <span className="msg-item-time">{shortTime(thread.last)}</span>
            </button>
          ))}
        </div>
      </aside>

      <section className="msg-main">
        {!activeKey ? (
          <div className="msg-empty-chat">
            <MessageCircle size={44} strokeWidth={1.5} />
            <p>{labels.selectConversation || 'Sélectionnez une conversation'}</p>
          </div>
        ) : (
          <>
            <div className="msg-main-head">
              <button type="button" className="msg-back" onClick={() => onSelect('')} aria-label="Retour">
                <ArrowLeft size={18} />
              </button>
              <span className="msg-avatar">{initials(title)}</span>
              <strong>{title}</strong>
            </div>
            {activeKey !== 'new' && (
              <div className="msg-bubbles" ref={bubblesRef} onScroll={handleScroll}>
                {messages.length === 0 && <p className="msg-empty-list">{labels.empty || 'Aucun message.'}</p>}
                {messages.map((m) => {
                  const mine = m.from?.role === myRole;
                  return (
                    <div key={m.id} className={`msg-bubble${mine ? ' is-mine' : ''}`}>
                      <p>{m.body}</p>
                      <small>{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</small>
                    </div>
                  );
                })}
              </div>
            )}
            <form className="msg-composer" onSubmit={onSend} noValidate>
              {activeKey === 'new' && contacts.length > 0 && (
                <div className="msg-contacts">
                  <div className="msg-contacts-head">
                    {contactsTitle && <p className="msg-contacts-title">{contactsTitle}</p>}
                    {!contactsOpen && pickedContact && (
                      <button
                        type="button"
                        className="msg-contact-change"
                        onClick={() => setContactsOpen(true)}
                      >
                        {labels.change || 'Changer'}
                      </button>
                    )}
                  </div>
                  {!contactsOpen && pickedContact ? (
                    <div className="msg-contacts-list">
                      <div className="msg-contact is-active">
                        <span className="msg-avatar msg-avatar--sm">{initials(pickedContact.name)}</span>
                        <span className="msg-item-meta">
                          <span className="msg-item-name">{pickedContact.name}</span>
                          {pickedContact.sub && <span className="msg-item-preview">{pickedContact.sub}</span>}
                        </span>
                        <Check size={16} className="msg-contact-check" />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="msg-contacts-search">
                        <Search size={15} />
                        <input
                          type="text"
                          value={contactQuery}
                          onChange={(e) => setContactQuery(e.target.value)}
                          placeholder={labels.searchContact || 'Rechercher…'}
                        />
                      </div>
                      <div className="msg-contacts-list">
                        {filteredContacts.length === 0 && (
                          <p className="msg-empty-list">{labels.empty || 'Aucun résultat.'}</p>
                        )}
                        {filteredContacts.map((c) => (
                          <button
                            key={c.key || '__default__'}
                            type="button"
                            className={`msg-contact${contactKey === c.key ? ' is-active' : ''}`}
                            onClick={() => {
                              onPickContact?.(c.key);
                              setContactsOpen(false);
                            }}
                          >
                            <span className="msg-avatar msg-avatar--sm">{initials(c.name)}</span>
                            <span className="msg-item-meta">
                              <span className="msg-item-name">{c.name}</span>
                              {c.sub && <span className="msg-item-preview">{c.sub}</span>}
                            </span>
                            {contactKey === c.key && <Check size={16} className="msg-contact-check" />}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="msg-composer-row">
                <textarea
                  rows={1}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder={labels.writeMessage || 'Écrire un message…'}
                />
                <button
                  type="submit"
                  className="msg-send"
                  disabled={busy || (activeKey === 'new' && requireContact && !contactKey)}
                  title={labels.send || 'Envoyer'}
                >
                  <Send size={18} />
                </button>
              </div>
            </form>
          </>
        )}
      </section>
    </div>
  );
}
